import store from 'store/index';

const { VITE_APP_POSTHOG_KEY, VITE_APP_CUSTOMER } = import.meta.env;

// Derived at runtime so the same build distinguishes towertexas vs towersemi.
const customerHostname = window.location.hostname;

// Cached init promise. identify/reset await it so they can never run on an
// uninitialized client, even though init is fire-and-forget at startup.
let initPromise = null;

// Cloud customers we want to track get VITE_APP_POSTHOG_KEY set at deploy time.
// On-prem builds omit it and tracking silently no-ops (future: self-hosted umami).
// Backend can also opt a tenant out via shouldDisableUserActivityTracking. Flag
// is read lazily at call time — no store subscription, no hot-reload.
function shouldTrack() {
  if (!VITE_APP_POSTHOG_KEY || VITE_APP_POSTHOG_KEY === 'null') {
    return false;
  }
  const flags = store.getState().common.featureFlags;
  return !flags?.shouldDisableUserActivityTracking;
}

export function initPostHog() {
  if (!shouldTrack() || initPromise) {
    return;
  }

  initPromise = (async () => {
    try {
      const { default: posthog } = await import('posthog-js');
      posthog.init(VITE_APP_POSTHOG_KEY, {
        api_host: 'https://app.posthog.com',
        capture_pageview: true,
        capture_pageleave: true,
        debug: import.meta.env.DEV,
      });
      return posthog;
    } catch {
      // Ad blocker, network failure, etc. — analytics must never break the app.
      return null;
    }
  })();
}

export async function identifyPostHogUser(user) {
  if (!shouldTrack() || !initPromise) {
    return;
  }

  const posthog = await initPromise;
  if (!posthog) {
    return;
  }

  posthog.identify(String(user.id), {
    email: user.email,
    name: [user.first_name, user.last_name].filter(Boolean).join(' '),
    customer_hostname: customerHostname,
    customer_name: VITE_APP_CUSTOMER,
  });

  // Group events by hostname — one group = one customer deployment
  posthog.group('customer', customerHostname, {
    hostname: customerHostname,
    customer_name: VITE_APP_CUSTOMER,
  });
}

export async function resetPostHog() {
  if (!initPromise) {
    return;
  }

  const posthog = await initPromise;
  if (!posthog) {
    return;
  }

  posthog.reset();
}
